import { buildOpenReplayLabel } from "@utils/openreplay";
import type { TrackParams } from "@/src/lib/tracking";
import type { BriefCardDropReason } from "./briefCardDrop";

export type BriefBatchInfo = {
  ids?: string[];
  event_ids?: string[];
  tracker_keys?: string[];
  tracker_ids?: string[];
};

export type BriefCardSwipeTrigger = "drag" | "dislike_button" | "skip_button";

const BRIEF_SCENE = "Brief";

/**
 * OpenReplay 自定义事件名 —— 规范 §3.1 snake_case。
 *
 * 2026-08-07：由 `brief-card-swipe` / `brief-end-feedback` 改名而来（A 组决策：改）。
 * ⚠️ 改名会断 OpenReplay 后台既有看板，**发版同期需运维同步这两个事件名**。
 *
 * 注：同一交互在 DOM 侧的 `data-tracking-eventname` 仍是连字符旧名。
 * 那个属性有两个消费方（已核对代码）：浏览器里被 GTM 触发器读走 → 派发 `logbeacon:log`
 * → logbeacon → 阿里云 SLS；App WebView 里被原生壳读走 → Firebase GA4。
 * 2026-08-07 决策：这两条 GA 通道**不删除**，长期并行 —— 所以旧名也就一直留着。
 * 不为它改名的理由不变：改 GA 字符串会动 GTM→SLS 与原生壳两个消费方的口径，
 * 而 OpenReplay 侧已经有规范名，跨通道对照靠的是「同一件事」不是「同一个字符串」。
 */
export const BRIEF_OPENREPLAY_EVENTS = {
  cardSwipe: "brief_card_swipe",
  /**
   * EndCard 的**整批**质量反馈。
   *
   * 2026-08-14：`brief_end_feedback` → `brief_endcard_feedback`。
   * 旧名里的 `end` 会被读成「结束反馈」，而它其实指 **EndCard**（划完那张末态卡），
   * 而且与自己的 click label `brief_endcard_feedback` 不同名。
   * ⚠️ 这个名字**不在下游契约里**（`downstream-contract.json` 只有 chat_card_view /
   * notification_button_feedback / chat_message_sent / chat_response_done / nav_screen_view），
   * 所以改名只影响 OpenReplay 后台看板 —— **发版同期需运维改看板**，与下游无关。
   */
  endFeedback: "brief_endcard_feedback",
  /** EndCard 的主动作：Feed me more / Try again / Explore More 某一项。 */
  endCardAction: "brief_endcard_action",
  /** Note 抽屉（Track 之后的草稿确认层）的三个动作。 */
  noteDrawerAction: "brief_notedrawer_action",
  /**
   * 下面三条是**给只有 Tap 的落点补的主动事件**（2026-08-17）。
   *
   * 点击消息（Tap）在协议层没有 payload 位，后台也不进自定义事件列表 ——
   * 只有 label 的落点等于「知道有人点了，但查不到任何上下文，也不好在事件面板里核对」。
   * 规则收紧为：**凡是用户动作，label 之外必须配一条主动事件**；纯容器/兜底 label 除外。
   */
  toolbarSelect: "brief_toolbar_select",
  membershipAction: "brief_membershipupsell_action",
  trackerToastAction: "brief_trackertoast_action",
  /**
   * 卡片曝光与正/负反馈**复用 chat 通知卡的事件名** —— brief 卡在下游眼里就是一种 notification，
   * 同名才能让它的干扰模型零改动吃到 brief。详见 `buildBriefCardViewPayload` 上方说明。
   */
  cardView: "chat_card_view",
  cardFeedback: "notification_button_feedback",
  /**
   * V3.0.6 信号标签三个新事件（fe-spec §3.4）：
   *
   * - cardDrop：渲染前过滤（`shouldDropCard`）整卡不出卡时的主动事件，
   *   选卡引擎补位天然成立，drop 只影响渲染不影响数据。
   * - swipeHintView / swipeHintDismiss：手势引导 coach mark 的曝光与消失
   *   （仅 H5 首次；无关闭按钮、无超时，出口是滑动或点击 → dismiss_reason
   *   取 swipe / tap，见 buildBriefSwipeHintDismissPayload）。
   */
  cardDrop: "brief_card_drop",
  swipeHintView: "brief_swipe_hint_view",
  swipeHintDismiss: "brief_swipe_hint_dismiss",
} as const;

export const BRIEF_CARD_ASK_ED_LABEL = buildOpenReplayLabel(
  BRIEF_SCENE,
  "Card",
  "AskEd",
);

/**
 * Brief / Calendar 分段控件的两个 tab。
 *
 * 改之前**两个 tab 共用** `brief_stockfinancials_click` —— 两个问题：
 * 1. 撞名，点哪个 tab 在 OpenReplay 上分不开（违反规范 §3.3「区分度必须写进名字」）
 * 2. 名字本身是从别处复制粘贴来的，`stockfinancials` 与 Brief/Calendar 毫无关系
 *
 * native `BriefScreen.kt` 的 Toolbar 也用同一组名字（改之前它叫 `brief_toolbar_select`，
 * 与 web 不同名、且同样两个 tab 不分）。
 */
export const BRIEF_TOOLBAR_LABELS = {
  brief: buildOpenReplayLabel(BRIEF_SCENE, "Toolbar", "Brief"),
  calendar: buildOpenReplayLabel(BRIEF_SCENE, "Toolbar", "Calendar"),
} as const;

export const BRIEF_CARD_STACK_SKIP_LABEL = buildOpenReplayLabel(
  BRIEF_SCENE,
  "CardStack",
  "Skip",
);

export const BRIEF_NOTE_DRAWER_CLOSE_LABEL = buildOpenReplayLabel(
  BRIEF_SCENE,
  "NoteDrawer",
  "Close",
);

export const BRIEF_NOTE_DRAWER_START_TRACKING_LABEL = buildOpenReplayLabel(
  BRIEF_SCENE,
  "NoteDrawer",
  "StartTracking",
);

export const BRIEF_NOTE_DRAWER_ADJUST_LABEL = buildOpenReplayLabel(
  BRIEF_SCENE,
  "NoteDrawer",
  "Adjust",
);

export function buildBriefCardOpenLabel(): string {
  return buildOpenReplayLabel(BRIEF_SCENE, "Card", "Open");
}

export function buildBriefCardOpenStateLabel({
  isTracked,
}: {
  isTracked: boolean;
}): string {
  // link 不进名字 —— 一个 URL 一个事件名没法统计，回放里本来也看得见。
  // tracked 状态是真区分度 → 进 action 段。与 KMP briefCardOpenStateLabel 同名。
  return buildOpenReplayLabel(
    BRIEF_SCENE,
    "Card",
    isTracked ? "OpenTracked" : "OpenUntracked",
  );
}

export function buildBriefCardTrackLabel(isTracked: boolean): string {
  // 区分度进 **action 段** —— 旧格式把 Tracked/Track 放在第四段（文案位），
  // 去掉第四段后两者会撞成同一个 label。这类"文案其实是动作"的必须挪上来。
  return buildOpenReplayLabel(
    BRIEF_SCENE,
    "Card",
    isTracked ? "Untrack" : "Track",
  );
}

export function buildBriefEndCardActionLabel(isError: boolean): string {
  // 同上：Try again / Feed me more 是两个动作，不是两句文案
  return buildOpenReplayLabel(
    BRIEF_SCENE,
    "EndCard",
    isError ? "Retry" : "FeedMore",
  );
}

/**
 * Explore More 的 label —— **所有项共用这一个，区分度走事件参数**。
 *
 * 曾经想把 `item.id` 拼进名字（`brief_endcard_explore_seemoving`），
 * 但那是**变量进名字**，`scripts/lint-click-labels.mjs` 的动态 label 棘轮会红：
 * 静态扫不出「这个变量是有界的」，一旦哪天出口项改成后端下发就会把基数打爆。
 *
 * 所以「点了哪一项」由 `brief_endcard_action{action:"explore", item_id, item_title}`
 * 回答 —— label 只说「点了某个出口项」，事件说「是哪一个」。
 */
export function buildBriefEndCardExploreLabel(): string {
  return buildOpenReplayLabel(BRIEF_SCENE, "EndCard", "Explore");
}

export function buildBriefMembershipSubscribeLabel(): string {
  return buildOpenReplayLabel(BRIEF_SCENE, "MembershipUpsell", "Subscribe");
}

export function buildBriefMembershipViewPlansLabel(): string {
  return buildOpenReplayLabel(BRIEF_SCENE, "MembershipUpsell", "ViewPlans");
}

/**
 * 划走事件的 payload。
 *
 * **带上卡片的内容维度**（`title` / `business_type` / `signal_event_type`）——
 * 改之前只有 `card_id`，于是「什么题材的卡最容易被划走」这类问题必须先把 card_id
 * join 回曝光事件才能回答，而 join 的两头键名还不一样（见下）。划走是 brief 上
 * **最高频**的动作，它自己带不上内容维度等于把最大的样本量废掉。
 *
 * `id` 与 `card_id` 同值双写：`card_id` 是既有看板在用的键（不动它），`id` 与
 * 曝光 `chat_card_view.id` / 反馈 `notification_button_feedback.notificationId`
 * 指的是同一个主键 —— 三个事件三套键名，跨事件 join 只能靠人肉映射。
 * 新增一个键不破坏任何消费方（破坏性的是改名和删除）。
 */
export function buildBriefCardSwipeOpenReplayPayload(
  trigger: BriefCardSwipeTrigger,
  direction: 1 | -1,
  card?: BriefCardFeedbackInfo & { businessType?: string },
): TrackParams {
  // trigger 已作为参数带在下面的 payload 里，label 不必再按它分叉
  return {
    trigger,
    direction: direction > 0 ? "right" : "left",
    card_id: card?.id,
    id: card?.id,
    title: card?.title,
    business_type: card?.businessType,
    signal_event_type: card?.event_type,
    tracker_id: card?.tracker_id,
    event_id: card?.eventId ?? card?.event_id,
    label: buildOpenReplayLabel(BRIEF_SCENE, "CardStack", "Swipe"),
  };
}

/**
 * 批次反馈的四个 id 数组**拍平成逗号串**。
 *
 * 走 `Track.event` 之后必须这么做 —— `TrackParams` 只收标量（规范 §3.2「标量优先，扁平比嵌套好查」）。
 *
 * 顺带修掉一个两端不一致：改之前 web 发的是**真数组**，而 native 的 `KmpOpenReplay.encode`
 * 对非标量走 `v.toString()`，落成 `"[a, b]"` —— 同一个字段两端两种格式，没法一起查。
 * 现在两端统一成 `join(",")`，也与 GA 通道（`BriefEndCard.tsx` 的 `ids?.join(",")`）一致。
 */
function joinIds(values?: string[]): string | undefined {
  // 防御式：payload 是在 `Track.event(...)` 的**参数位**构造的，抛异常落在门面 try/catch
  // **外面** —— 会直接崩掉点击回调。「埋点绝不影响业务」在这里必须自己保证。
  // 上游 `useBriefPageState` 用 `item.id!` 非空断言，运行时元素仍可能是 undefined。
  if (!Array.isArray(values)) {
    return typeof values === "string" && values ? values : undefined;
  }
  const kept = values.filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );
  return kept.length ? kept.join(",") : undefined;
}

export function buildBriefEndFeedbackOpenReplayPayload(
  sentiment: "good" | "poor",
  batchInfo?: BriefBatchInfo,
): TrackParams {
  return {
    sentiment,
    label: buildOpenReplayLabel("Brief", "EndCard", "Feedback"),
    ids: joinIds(batchInfo?.ids),
    event_ids: joinIds(batchInfo?.event_ids),
    tracker_keys: joinIds(batchInfo?.tracker_keys),
    tracker_ids: joinIds(batchInfo?.tracker_ids),
  };
}

/** Brief / Calendar 两个 tab —— 与 native `BriefSubTab.key` 一致。 */
export type BriefToolbarTab = "brief" | "calendar";

/** 顶部分段控件切 tab。label 只说「点了 Toolbar」，是哪个 tab 由这条事件回答。 */
export function buildBriefToolbarSelectPayload(
  tab: BriefToolbarTab,
): TrackParams {
  return { tab };
}

/** 会员卡两个 CTA 的取值 —— 与 native `BriefMembershipAction` 逐字一致。 */
export type BriefMembershipAction = "subscribe" | "view_plans";

/** 额度耗尽时末态卡贴底会员卡上的两个 CTA。 */
export function buildBriefMembershipActionPayload(
  action: BriefMembershipAction,
): TrackParams {
  return { action };
}

/** 建 tracker 成功 toast 上的 View（点了才进 tracker 详情）。 */
export function buildBriefTrackerToastActionPayload(
  trackerId?: string,
  eventId?: string,
): TrackParams {
  return { action: "view", tracker_id: trackerId, event_id: eventId };
}

/** EndCard 主动作的取值 —— 与 native `BriefEndCardAction` 逐字一致。 */
export type BriefEndCardAction = "feed_more" | "retry" | "explore";

/**
 * EndCard 主动作（Feed me more / Try again / Explore More 某一项）。
 *
 * 改之前这三处**只有 click label**，所以「这一批看完之后用户往哪走」只有计数：
 * 不知道是哪一批（batch id 就在同一个组件里）、也不知道点的是哪个出口项。
 *
 * `action` 已经把错误态区分开（`retry` ⇔ 出错重试），不再单发一个 `is_error`。
 */
export function buildBriefEndCardActionPayload(
  action: BriefEndCardAction,
  batchInfo?: BriefBatchInfo,
  item?: { id?: string; title?: string },
): TrackParams {
  // 注：这条是**批次级**动作（末态卡不属于任何一张卡），所以没有单个 `event_id`，
  // 对应的是下面那串 `event_ids`（整批的信号事件 id）。
  return {
    action,
    item_id: item?.id,
    item_title: item?.title,
    // 与 `brief_endcard_feedback` 同一组批次键，便于「这批评了 poor 的人后来点了什么」
    ids: joinIds(batchInfo?.ids),
    event_ids: joinIds(batchInfo?.event_ids),
    tracker_keys: joinIds(batchInfo?.tracker_keys),
    tracker_ids: joinIds(batchInfo?.tracker_ids),
  };
}

/** Note 抽屉三个动作的取值 —— 与 native `BriefNoteDrawerAction` 逐字一致。 */
export type BriefNoteDrawerAction = "close" | "start_tracking" | "adjust";

/**
 * Note 抽屉（点 Track 之后的草稿确认层）的动作。
 *
 * 改之前只有 click label，而这一层**拿得到整张卡** —— 于是
 * 「点了 Track 的人里有多少真的建成了 tracker」这个转化率算不出来。
 *
 * `id` 取的是 **brief 记录 id**（`record_id`）而不是草稿 id：只有它能和曝光
 * `chat_card_view.id` / 反馈 `notification_button_feedback.notificationId` 配上。
 * 草稿 id 另放 `draft_id` —— 两个 id 都留着，不然排障时分不清是哪一层的对象。
 */
export function buildBriefNoteDrawerActionPayload(
  card:
    | { record_id?: string; id?: string; title?: string; eventId?: string }
    | null
    | undefined,
  action: BriefNoteDrawerAction,
): TrackParams {
  return {
    action,
    id: card?.record_id,
    draft_id: card?.id,
    title: card?.title,
    event_id: card?.eventId,
  };
}

export type BriefCardFeedbackInfo = {
  id?: string;
  /**
   * 信号事件 id —— 解析自 `<dynamic_ui>` 的 `container.props.eventId`
   * （形如 `evt:news_ticker:zijin-gold-…:1786940714384`），缺失时回落记录级 `event_id`。
   * 见 `parseUrl.ts: parseEventId`。**brief 每条事件都要带**：它是把埋点与信号引擎
   * 那侧的事件对上的唯一键，`id`（记录 id）在信号侧不存在。
   */
  eventId?: string;
  event_id?: string;
  tracker_key?: string;
  tracker_id?: string;
  title?: string;
  event_type?: string;
};

/**
 * brief 卡在下游眼里就是一种 **notification** —— 所以它的曝光与反馈复用 chat 通知卡那套
 * 事件名与字段，`edgen_events` 的干扰模型零改动就能把 brief 算进来。
 * 自造一套 `brief_*` 名字的话，下游得改代码才吃得到。
 *
 * 三个事件（两端同名同字段）：
 *
 * | 时机 | 事件 | 关键字段 |
 * |---|---|---|
 * | 卡片露出 | `chat_card_view` | `id` · `card_scene="brief"` |
 * | Track / Ask Ed / View details / Setup with Ed / 整卡点开 | `notification_button_feedback` | `type="positive"` · `cta_id` · `notificationId` |
 * | Dislike | `notification_button_feedback` | `type="negative"` · `cta_id="dislike"` |
 *
 * ## 字段约束（三条都踩过坑）
 *
 * - `notificationId` 是 camelCase，违反规范 §3.2，但**下游 settler 直接读它**做曝光↔反馈配对，
 *   **因下游依赖而冻结**，不许顺手清理
 * - `signal_event_type` 而非 `event_type` —— 后者是 envelope 保留键，同名会被**无条件静默覆盖**
 * - `card_scene` 而非 `scene` —— 同理，envelope 会注入 `scene`（条件覆盖）
 */

/** brief 卡的**曝光**（对齐 chat 通知卡 `chat_card_view`）。 */
export function buildBriefCardViewPayload(
  card: BriefCardFeedbackInfo & {
    /** `parseEyebrow` 产出（结构同 `BriefEyebrowInfo`）；非 eyebrow 卡不传。 */
    eyebrow?: { text?: string; cardCategory?: string } | null;
  },
  /**
   * V3.0.6 扩展维度（fe-spec §3.4）：
   * - `card_index`：卡在批次中的序号
   * - `is_paid`：alpha_pick 卡的付费态（`useMemberUser`）
   * - `slot_fallback`：是否发生槽位兜底渲染
   */
  extras?: {
    card_index?: number;
    is_paid?: boolean;
    slot_fallback?: boolean;
  },
): TrackParams {
  return {
    id: card.id,
    card_scene: "brief",
    title: card.title,
    signal_event_type: card.event_type,
    event_id: card.eventId ?? card.event_id,
    // V3.0.6：eyebrow 维度 —— article 卡带 card_category 原值，其余卡只有 eyebrow_text
    eyebrow_text: card.eyebrow?.text,
    card_category: card.eyebrow?.cardCategory,
    card_index: extras?.card_index,
    is_paid: extras?.is_paid,
    slot_fallback: extras?.slot_fallback,
  };
}

/** 渲染前过滤事件（`brief_card_drop`）的 payload；reason 取值与 `briefCardDrop.ts` 一致。 */
export function buildBriefCardDropPayload(
  card: (BriefCardFeedbackInfo & { businessType?: string }) | null | undefined,
  reason: BriefCardDropReason,
): TrackParams {
  return {
    card_id: card?.id,
    card_type: card?.businessType,
    drop_reason: reason,
    // 贯穿字段（P4）：drop 也发生在具体一张 record 上，event_id 照带
    event_id: card?.eventId ?? card?.event_id,
  };
}

/** 手势引导曝光（`brief_swipe_hint_view`）：card_type = 栈顶卡型。 */
export function buildBriefSwipeHintViewPayload(cardType?: string): TrackParams {
  return { card_type: cardType };
}

/**
 * 手势引导消失（`brief_swipe_hint_dismiss`）。两个出口（2026-08-25 起，
 * Figma 104014:13787 批注）：
 * - swipe：划走 hint 本身（主出口），或 hint 可见期间卡片被划走的兜底出口，
 *   带 direction；无关闭按钮、无超时（PRD S2.2）不变。
 * - tap：点击 hint 任意处，只消耗为「关闭提示」（150ms ease-in 淡出，
 *   见 BriefSwipeHint），无 direction —— 取代 PRD S2.1 的「点击不关闭」。
 */
export function buildBriefSwipeHintDismissPayload(
  reason: "swipe" | "tap",
  direction?: 1 | -1,
): TrackParams {
  return {
    dismiss_reason: reason,
    ...(direction !== undefined && {
      direction: direction > 0 ? "right" : "left",
    }),
  };
}

/**
 * brief 卡的正 / 负反馈。
 *
 * `type="positive"` 的触发点是**用户主动表达兴趣**的五个动作（Track / Ask Ed /
 * View details / Setup with Ed / 整卡点开）；`negative` 只有 dislike。
 * 划卡（swipe）**不算负反馈** —— 划走可能只是"这条已读"，与"不感兴趣"是两件事，
 * 它有自己的 `brief_card_swipe` 事件（native 侧注释里也是这个判据）。
 */
export function buildBriefCardFeedbackPayload(
  card: BriefCardFeedbackInfo & { businessType?: string },
  ctaId: BriefCardCtaId,
  type: "positive" | "negative",
): TrackParams {
  return {
    content_scene: "brief",
    cta_id: ctaId,
    type,
    label: card.title,
    notificationId: card.id,
    signal_event_type: card.event_type,
    // 产品侧的内容偏好维度 —— **合并进这一条，不另发 brief_card_action**。
    //
    // 曾经分成两条（下游 schema 一条 + 产品分析一条），理由是「不污染下游 schema」。
    // 那个理由站不住：5 个字段里 3 个重复（card id / cta / signal_event_type），
    // 第二条只为 business_type + tracker_id 两个新字段而存在 —— 属规范 §5 要防的重复采集。
    // 而**下游只读它关心的 key，多余的 key 跟着走不会坏任何东西**：破坏性的是改名和删除，不是新增。
    business_type: card.businessType,
    tracker_id: card.tracker_id,
    event_id: card.eventId ?? card.event_id,
  };
}

/**
 * 反馈事件的 `cta_id` 取值 —— 有界枚举。
 *
 * 前五个与 native `BriefCatId` 一一对应；[BRIEF_CTA_OPEN_CARD] 不是按钮 ——
 * 它是**整卡点开**，没有对应的 catId（两端都由卡片容器的点击手势触发）。
 */
export type BriefCardCtaId =
  | "track"
  | "ask_ed"
  | "view_detail"
  | "setup_with_ed"
  | "dislike"
  | "open_card";

/**
 * 整卡点开详情的 `cta_id`。
 *
 * 改之前这个动作**只有一个 click label**（`brief_card_open(un)tracked`）——
 * click 消息在协议层没有 payload，所以「点开了哪张卡」查不到。
 * 而点开详情是 brief 上最强的正向意图（比 Track 更轻、比划走更明确），
 * 缺了它下游干扰模型算出的正反馈率是偏低的。
 *
 * 复用 `notification_button_feedback` 而不新起事件名：下游只按
 * `notificationId` + `type` + `cta_id` 读，多一个 cta_id 取值它零改动就吃到。
 */
export const BRIEF_CTA_OPEN_CARD = "open_card" satisfies BriefCardCtaId;

export const BRIEF_CARD_FEEDBACK_POOR_LABEL = buildOpenReplayLabel(
  "Brief",
  "Card",
  "Feedback",
);

/** 单卡哭脸负反馈：与 EndCard 批次字段对齐，值为单条 record */
export function getBriefCardPoorFeedbackTrackingProps(
  card: BriefCardFeedbackInfo,
) {
  return {
    "data-tracking-eventname": "brief-card-feedback",
    "data-tracking-event-key-sentiment": "poor",
    "data-tracking-event-key-ids": card.id,
    "data-tracking-event-key-event_ids": card.event_id,
    "data-tracking-event-key-tracker_keys": card.tracker_key,
    "data-tracking-event-key-tracker_ids": card.tracker_id,
    "data-openreplay-label": BRIEF_CARD_FEEDBACK_POOR_LABEL,
  };
}
